import { STATIC_PATH_FALLBACK } from 'constants/environment';
import NotFound from 'components/Pages/NotFound';
import { pageMapping } from 'components/Pages/pageMapping';
import Layout from 'components/ui/Layout';
import config from 'config';
import getPageData from 'data/page';
import { skipServerSideBuild } from 'data/page/utils';
import { contentfulClientPreview } from 'services/contentful';
import Head from 'next/head';
import type { GetStaticPaths, GetStaticProps } from 'next';
import type { ParsedUrlQuery } from 'querystring';
import type { ElementType, ReactNode } from 'react';
import type { ILocationPageFields } from 'types/contentful/generated';
import type {
  ConfigLocale,
  Level4PageEntry,
  Level4Path,
  LocationTreatmentPageComponentParams,
  PageData,
  RoutesMap,
} from 'types/PageData';
import routesCache from '.cache/routes.json';

const routesMap = routesCache as unknown as RoutesMap;

const Level4 = ({
  entry,
  clinics,
  cta,
  doctors,
  contentType,
  preview,
  alternateSlugs,
  footer,
  beforeAfterPhotos,
  noMetaTitle,
  brand,
}: LocationTreatmentPageComponentParams<Level4PageEntry>): ReactNode => {
  if (!entry) {
    return <NotFound brand={brand} footer={footer} />;
  }

  const PageType = pageMapping[contentType] as ElementType;
  const { metaDescription, metaTitle } = entry.fields || entry;

  return (
    <Layout
      preview={preview}
      // searchIndex={!entry.disableIndexing && !entry.fields.disableIndexing}
      title={metaTitle || ''}
      metaDescription={metaDescription || ''}
      alternateSlugs={alternateSlugs}
      footer={footer}
      noMetaTitle={noMetaTitle || ''}
      brand={brand}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            // eslint-disable-next-line
            // @ts-ignore
            __html: JSON.stringify({ dateModified: entry?.sys?.publishedAt }),
          }}
        />
      </Head>
      <PageType
        entry={entry}
        clinics={clinics}
        cta={cta}
        doctors={doctors}
        beforeAfterPhotos={beforeAfterPhotos}
      />
    </Layout>
  );
};

interface Level4StaticParams extends ParsedUrlQuery {
  page: string;
  level_4: string;
  locale: string;
}

export const getStaticProps: GetStaticProps<
  PageData,
  Level4StaticParams
> = async ({ params, preview = false, previewData }) => {
  if (params) {
    const { props } = await getPageData(params, preview, previewData);

    if (!props.entry && !props.isCustomRoute) {
      return {
        notFound: true,
      };
    }

    return {
      props,
      revalidate: config.defaults.secondsForPageRevalidation,
    };
  }

  throw new Error("Couldn't build page, params are missing");
};

export const getStaticPaths: GetStaticPaths<Level4StaticParams> = async () => {
  if (skipServerSideBuild()) {
    return {
      paths: [],
      fallback: STATIC_PATH_FALLBACK,
    };
  }

  const paths: Level4Path[] = [];

  const getEntries = config.locales.map(async (locale: ConfigLocale) => {
    const entries =
      await contentfulClientPreview.getEntries<ILocationPageFields>({
        'sys.contentType.sys.id[in]': config.level_4.contentTypes.join(','),
        locale: locale.contentful,
        limit: 1000,
      });

    entries.items.forEach((item) => {
      if (!item.fields.disableLocale && item.fields.slug.match('/')) {
        if (
          item.sys.contentType.sys.id === 'locationPage' &&
          item.fields.speciality &&
          item.fields.treatment
        ) {
          const slug = item.fields.slug.split('/');
          paths.push({
            params: {
              locale: locale.main,
              page: item.fields.speciality.fields.slug,
              subpage: item.fields.treatment.fields.slug,
              level_3: slug[0],
              level_4: slug[1],
            },
          });
        }
      }
    });
  });

  await Promise.all(getEntries);

  paths.push(
    ...Object.keys(routesMap)
      .filter((route) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return routesMap[route]?.slugParts?.length === 5;
      })
      .map((route) => {
        const routeData = routesMap[route];

        const [locale, page, subpage, level_3, level_4] =
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          routeData?.slugParts || [];

        return {
          params: {
            locale,
            page,
            subpage,
            level_3,
            level_4,
          },
        };
      })
      .filter(
        ({ params: { locale, page, subpage, level_3, level_4 } }) =>
          locale && page && subpage && level_3 && level_4,
      ),
  );

  return {
    paths,
    fallback: 'blocking',
  };
};

export default Level4;
